/**
 * W2 — Per-client platform integrations API client.
 *
 * Thin fetch wrapper matching `notifications-api.ts` / `identity-api.ts`
 * conventions. RTK Query is intentionally NOT used — kept consistent with the
 * rest of the agency-portal API surface until the Phase 6 (deferred) unify.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * The token lives in Redux ONLY (never localStorage).
 *
 * Endpoints (W2 backend — built to the agreed contract):
 *   GET    /api/v1/shops/:slug/integrations
 *   GET    /api/v1/shops/:slug/integrations/:platform/connect-url
 *   DELETE /api/v1/shops/:slug/integrations/:platform
 *   POST   /api/v1/shops/:slug/integrations/:platform/test
 *
 * The OAuth flow: the FE opens the `connect-url` (same tab or popup); the
 * backend completes the OAuth handshake and redirects back to
 * `/integrations?connected={platform}`. The page detects that query param,
 * surfaces a success toast and re-fetches the list.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/**
 * `mode` distinguishes how the platform talks to ADUP:
 *   - `official`: official, OAuth-backed first-party integration.
 *   - `bridge`:   the legacy bridge MCP connection.
 *
 * `oauth_supported` gates whether the "Connect" button drives the connect-url
 * OAuth flow. When false (e.g. ADUP-managed platforms), the card surfaces an
 * informational note instead of a connect action.
 */
export type IntegrationMode = 'official' | 'bridge';

export interface IntegrationPlatform {
  key: string;
  name: string;
  connected: boolean;
  connected_at: string | null;
  account_label: string | null;
  mode: IntegrationMode;
  oauth_supported: boolean;
}

export interface IntegrationsListResponse {
  platforms: IntegrationPlatform[];
}

export interface ConnectUrlResponse {
  url: string;
}

export interface DisconnectResponse {
  ok: boolean;
}

export interface TestResult {
  ok: boolean;
  detail?: string;
}

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[integrations-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function shopPath(slug: string): string {
  return `shops/${encodeURIComponent(slug)}/integrations`;
}

// ---------- public API ----------

/** List the platform integrations available/connected for a shop. */
export async function listIntegrations(
  slug: string,
  token?: string
): Promise<IntegrationPlatform[]> {
  const raw = await request<
    | IntegrationsListResponse
    | IntegrationPlatform[]
    | { data?: IntegrationsListResponse }
  >(shopPath(slug), { method: 'GET' }, token);

  if (Array.isArray(raw)) return raw;

  const body = unwrap<IntegrationsListResponse>(
    raw as { data?: IntegrationsListResponse }
  );
  return body?.platforms ?? [];
}

/**
 * Fetch the OAuth connect URL for a given platform.
 *
 * The FE opens the returned `url` (same tab or popup). The backend completes
 * the OAuth handshake server-side and redirects back to
 * `/integrations?connected={platform}`.
 *
 * `shopDomain` is required for PER-SHOP providers — currently only Shopify,
 * whose authorize endpoint lives on the merchant's own store
 * (`https://{shop}/admin/oauth/authorize`) rather than a fixed vendor host. The
 * backend validates + pins it to `*.myshopify.com` and binds it into the signed
 * OAuth state; omitting it yields 422 `shop_domain_required`.
 */
export async function getConnectUrl(
  slug: string,
  platform: string,
  token?: string,
  shopDomain?: string
): Promise<ConnectUrlResponse> {
  const query = shopDomain
    ? `?shop_domain=${encodeURIComponent(shopDomain)}`
    : '';

  const raw = await request<
    ConnectUrlResponse | { data?: ConnectUrlResponse }
  >(
    `${shopPath(slug)}/${encodeURIComponent(platform)}/connect-url${query}`,
    { method: 'GET' },
    token
  );
  return unwrap<ConnectUrlResponse>(raw);
}

/**
 * Fetch the OAuth connect URL for a BRAND-OWNED Shopify app
 * (bring-your-own-app): the agency created its own custom-distribution app
 * for this store and registered ADUP's public callback as its redirect URL.
 *
 * POST (not GET) because the body carries the app's client secret — secrets
 * never belong in a query string. The backend stores the pair encrypted on
 * the brand credential row and returns an authorize URL built with the
 * brand's client_id; the callback completes with the same pair. On
 * reconnect the pair may be omitted — the stored one is reused.
 */
export async function getAgencyAppConnectUrl(
  slug: string,
  shopDomain: string,
  clientId?: string,
  clientSecret?: string,
  token?: string
): Promise<ConnectUrlResponse> {
  const body: Record<string, string> = { shop_domain: shopDomain };
  if (clientId) body.client_id = clientId;
  if (clientSecret) body.client_secret = clientSecret;

  const raw = await request<
    ConnectUrlResponse | { data?: ConnectUrlResponse }
  >(
    `${shopPath(slug)}/shopify/agency-app/connect-url`,
    { method: 'POST', body: JSON.stringify(body) },
    token
  );
  return unwrap<ConnectUrlResponse>(raw);
}

/** Disconnect a platform integration for a shop. */
export async function disconnectIntegration(
  slug: string,
  platform: string,
  token?: string
): Promise<DisconnectResponse> {
  const raw = await request<
    DisconnectResponse | { data?: DisconnectResponse }
  >(
    `${shopPath(slug)}/${encodeURIComponent(platform)}`,
    { method: 'DELETE' },
    token
  );
  return unwrap<DisconnectResponse>(raw);
}

/** Run a connectivity test against a connected platform integration. */
export async function testIntegration(
  slug: string,
  platform: string,
  token?: string
): Promise<TestResult> {
  const raw = await request<TestResult | { data?: TestResult }>(
    `${shopPath(slug)}/${encodeURIComponent(platform)}/test`,
    { method: 'POST' },
    token
  );
  return unwrap<TestResult>(raw);
}
